import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Stack
} from '@mui/material';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import TabIcon from '@mui/icons-material/Tab';
import Career360Wheel from './Career360Wheel';
import CareerJourneyPath100 from './CareerJourneyPath100';
import { useLandingPageAppearance } from '../../context/LandingPageAppearanceContext';

const VIEW_TABS = 'tabs';
const VIEW_FREESTYLE = 'freestyle';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" id={`career-journey-tabpanel-${index}`} aria-labelledby={`career-journey-tab-${index}`} sx={{ pt: 3 }}>
      {children}
    </Box>
  );
}

function LifecycleIntro({ textPrimary, textSecondary }) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ display: 'block', textAlign: 'center', color: textSecondary, fontWeight: 700, letterSpacing: 0.14 }}
      >
        End-to-end lifecycle
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, textAlign: 'center', mb: 1.5, color: textPrimary }}>
        360° of the human journey
      </Typography>
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          color: textSecondary,
          mb: 1,
          maxWidth: 720,
          mx: 'auto',
          lineHeight: 1.7
        }}
      >
        From campus to career and beyond — project intern, final-year intern, job search, upskilling, placement,
        certifications, side projects, and ongoing challenges. ERP4C supports every stage in one continuous loop.
      </Typography>
    </>
  );
}

function LifecycleFootnote({ textSecondary }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        textAlign: 'center',
        color: alpha(textSecondary, 0.75),
        mt: 2,
        maxWidth: 520,
        mx: 'auto'
      }}
    >
      The journey repeats: learn, apply, grow, and take on new challenges — a full circle around the individual.
    </Typography>
  );
}

export default function CareerJourneySection() {
  const { careerTheme } = useLandingPageAppearance();
  const textPrimary = careerTheme.textPrimary;
  const textSecondary = careerTheme.textSecondary;
  const accent = careerTheme.decorColor1;
  const panelBorder = careerTheme.panelBorder;

  const [viewMode, setViewMode] = useState(VIEW_TABS);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box
      id="career-360"
      sx={{
        py: { xs: 6, md: 9 },
        bgcolor: alpha(careerTheme.panelBg, 0.55),
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: careerTheme.panelBorder,
        transition: 'background-color 0.35s ease, border-color 0.35s ease'
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="center"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: viewMode === VIEW_TABS ? 2 : 3 }}
        >
          <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 500 }}>
            Explore the journey
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, v) => v && setViewMode(v)}
            aria-label="Career journey layout"
            sx={{
              bgcolor: alpha(careerTheme.cardBg, 0.85),
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                color: textSecondary,
                borderColor: panelBorder,
                px: 2,
                py: 0.75,
                '&.Mui-selected': {
                  bgcolor: alpha(accent, 0.45),
                  color: textPrimary,
                  '&:hover': { bgcolor: alpha(accent, 0.55) }
                }
              }
            }}
          >
            <ToggleButton value={VIEW_TABS} aria-label="Tab view">
              <TabIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Tabs
            </ToggleButton>
            <ToggleButton value={VIEW_FREESTYLE} aria-label="Freestyle stacked view">
              <ViewAgendaIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Freestyle
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {viewMode === VIEW_TABS ? (
          <Box>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              centered
              variant="fullWidth"
              sx={{
                maxWidth: 560,
                mx: 'auto',
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  bgcolor: accent
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: alpha(textSecondary, 0.85),
                  minHeight: 48,
                  '&.Mui-selected': { color: textPrimary }
                }
              }}
            >
              <Tab id="career-journey-tab-0" label="360° of the human journey" aria-controls="career-journey-tabpanel-0" />
              <Tab
                id="career-journey-tab-1"
                label="Walk the career progression line"
                aria-controls="career-journey-tabpanel-1"
              />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <LifecycleIntro textPrimary={textPrimary} textSecondary={textSecondary} />
              <Career360Wheel />
              <LifecycleFootnote textSecondary={textSecondary} />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <CareerJourneyPath100 />
            </TabPanel>
          </Box>
        ) : (
          <Stack spacing={{ xs: 6, md: 8 }}>
            <Box>
              <LifecycleIntro textPrimary={textPrimary} textSecondary={textSecondary} />
              <Career360Wheel />
              <LifecycleFootnote textSecondary={textSecondary} />
            </Box>
            <Box>
              <CareerJourneyPath100 />
            </Box>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
